const CAP22 = {
  id: "fundamentos-blockchain-22",
  slug: "que-podemos-hacer-con-los-tokens-erc20",
  title: "¿Qué Podemos Hacer con los Tokens ERC-20?",
  sections: [
    {
      id: "hero",
      heading: "Los Tokens ERC-20: La Infraestructura de la Nueva Economía Digital",
      text: `Los tokens **ERC-20** representan una de las herramientas más versátiles del ecosistema blockchain.  
Se utilizan para transferir valor, administrar economías digitales, ejecutar contratos inteligentes y habilitar servicios dentro de miles de plataformas descentralizadas.  
Al ser compatibles con la red Ethereum, heredan sus principios de **seguridad, transparencia e interoperabilidad**, 
y pueden interactuar sin fricciones con billeteras, mercados, juegos, universidades, bancos o gobiernos que integren tecnología Web3.  

En esencia, un token ERC-20 puede verse como una unidad de valor programable: una moneda, un punto de recompensa, una acción digital, 
o incluso una representación de energía o propiedad intelectual.  
En 2025, los tokens ERC-20 son la columna vertebral de la economía digital y del nuevo Internet de valor.`
    },

    {
      id: "funciones-basicas",
      heading: "Funciones Básicas de los Tokens ERC-20",
      text: `Además de representar valor, los tokens ERC-20 permiten realizar una variedad de operaciones dentro de la red Ethereum, tales como:`,
      list: [
        {
          title: "1. Consultar el suministro total (totalSupply)",
          text: `Permite conocer cuántos tokens existen en circulación, garantizando transparencia y control monetario.`
        },
        {
          title: "2. Transferir fondos (transfer)",
          text: `Los usuarios pueden enviar tokens entre direcciones sin intermediarios, con registro público e inmutable en la blockchain.`
        },
        {
          title: "3. Verificar saldos (balanceOf)",
          text: `Facilita la consulta instantánea de los saldos de una cuenta en cualquier momento, de forma segura y verificable.`
        },
        {
          title: "4. Autorizar a otras DApps (approve / transferFrom)",
          text: `Permite que contratos o aplicaciones descentralizadas administren tokens en nombre del usuario, 
como sucede en plataformas DeFi, juegos o exchanges.`
        }
      ]
    },

    {
      id: "clasificacion",
      heading: "Tipos de Tokens ERC-20 y sus Usos Actuales",
      text: `Los tokens ERC-20 se clasifican en varias categorías según su función y respaldo económico.  
Cada tipo responde a una necesidad concreta dentro del ecosistema blockchain.  
A continuación se analizan los principales y los casos reales donde se utilizan en 2025.`
    },

    {
      id: "stablecoins",
      heading: "Stablecoins: Estabilidad y Transferencias Globales",
      text: `Las **stablecoins** son tokens ERC-20 diseñados para mantener un valor estable al estar vinculados a una moneda fiduciaria como el dólar o el euro.  
Se han convertido en uno de los pilares de la economía digital, permitiendo transferencias internacionales casi instantáneas, 
pagos sin fronteras y estabilidad en los ecosistemas DeFi.`,
      list: [
        {
          title: "Principales características",
          text: `- Mantienen paridad 1:1 con una moneda tradicional (USD, EUR, MXN, etc.).  
- Reducen la volatilidad típica de las criptomonedas.  
- Se usan para trading, pagos y ahorro digital estable.`
        },
        {
          title: "Ejemplos destacados en 2025",
          text: `1. **USDT (Tether)** — continúa como la stablecoin más usada del mundo, con soporte en Ethereum, Polygon y Arbitrum.  
2. **USDC (Circle)** — adoptada por bancos y universidades para pagos institucionales y programas de becas tokenizadas.  
3. **DAI (MakerDAO)** — descentralizada y garantizada con activos criptográficos, sigue siendo el estándar en plataformas DeFi.  
4. **EUROC (Circle)** — stablecoin europea integrada por empresas fintech en España, Francia y Alemania.  
5. **MXNT (Tether México)** — versión latinoamericana respaldada en pesos mexicanos, utilizada en remesas digitales y pagos P2P.`
        },
        {
          title: "Empresas que las usan",
          text: `- **PayPal** y **Visa** integran stablecoins ERC-20 para pagos transfronterizos.  
- **Binance**, **Kraken** y **Coinbase** operan la mayoría de sus mercados con pares ERC-20.  
- **UVEG Blockchain Campus** y **CoachBlockchain** usan USDC para emitir microcredenciales certificadas.`
        }
      ]
    },

    {
      id: "security-tokens",
      heading: "Security Tokens: Propiedad Digital y Financiamiento Regulado",
      text: `Los **security tokens** representan activos financieros tradicionales (acciones, bonos, inmuebles, obras de arte) dentro de la blockchain.  
Su emisión está sujeta a regulaciones, ya que otorgan derechos económicos o de voto a los inversionistas.  
El estándar ERC-20 es ideal para estos activos por su transparencia, trazabilidad y facilidad de auditoría.`,
      list: [
        {
          title: "Casos de uso reales",
          text: `- **Securitize** tokeniza participaciones empresariales para inversionistas institucionales.  
- **INX Token** cotiza en mercados regulados de EE.UU. representando acciones de la compañía.  
- **RealT** fracciona propiedades inmobiliarias en EE.UU., permitiendo inversión global en bienes raíces mediante ERC-20.  
- **Tokeny Solutions** (Luxemburgo) ofrece infraestructura para emisión y cumplimiento regulatorio (KYC/AML).`
        },
        {
          title: "Ventajas del modelo",
          text: `- Acceso global a inversiones antes restringidas.  
- Pagos de dividendos automáticos mediante contratos inteligentes.  
- Registro inmutable de propiedad y transferencias.  
- Reducción de costos legales y operativos.`
        },
        {
          title: "Empresas y entidades que lo aplican",
          text: `1. **BlackRock Digital Assets** — gestiona participaciones tokenizadas de fondos institucionales.  
2. **Santander Digital Assets** — prueba piloto de bonos corporativos tokenizados.  
3. **JP Morgan Onyx** — integra ERC-20 para pruebas de liquidez en mercados privados.`
        }
      ]
    },

    {
      id: "utility-tokens",
      heading: "Utility Tokens: Energía del Ecosistema Descentralizado",
      text: `Los **utility tokens** son los más comunes del mercado y representan acceso, funcionalidad o participación dentro de una plataforma.  
No están respaldados por activos externos, pero ofrecen valor de uso dentro de sus ecosistemas.`,
      list: [
        {
          title: "Ejemplos de utility tokens ERC-20",
          text: `1. **UNI (Uniswap)** — permite votar sobre decisiones del protocolo y participar en la gobernanza.  
2. **LINK (Chainlink)** — facilita la conexión entre contratos inteligentes y datos del mundo real (oráculos).  
3. **MANA (Decentraland)** — se utiliza en el metaverso para comprar terrenos, objetos y participar en eventos.  
4. **BAT (Basic Attention Token)** — recompensa la atención de los usuarios en el navegador Brave.  
5. **COMP (Compound)** — ofrece participación en la toma de decisiones del protocolo de préstamos DeFi.`
        },
        {
          title: "Usos prácticos en 2025",
          text: `- En plataformas educativas como **CoachBlockchain**, los tokens ERC-20 se utilizan como recompensas de aprendizaje.  
- En la industria del gaming, **Immutable X** y **Axie Infinity** gestionan economías internas totalmente basadas en utility tokens.  
- En el sector energético, **PowerLedger** usa ERC-20 para el intercambio de energía entre usuarios.`
        }
      ]
    },

    {
      id: "governance-tokens",
      heading: "Governance Tokens: Democracia Digital en Blockchain",
      text: `Una evolución de los utility tokens son los **tokens de gobernanza**, que otorgan a los titulares la posibilidad de votar, proponer cambios 
y participar en la dirección futura de proyectos descentralizados (DAOs).`,
      list: [
        {
          title: "Ejemplos líderes en 2025",
          text: `- **AAVE** — gestiona políticas de préstamos y recompensas del protocolo.  
- **MKR (MakerDAO)** — los poseedores votan sobre los parámetros de estabilidad del DAI.  
- **ENS Token** — regula la administración de nombres de dominio descentralizados en Ethereum.  
- **Arbitrum DAO** — comunidad que decide la evolución de la red Layer 2 más popular.`
        },
        {
          title: "Casos de aplicación empresarial",
          text: `Empresas de tecnología, universidades y organizaciones sociales utilizan modelos DAO con tokens ERC-20 para votaciones internas, 
asignación de recursos y toma de decisiones colectivas.  
Ejemplo: **Gitcoin DAO** y **Optimism Collective** financian proyectos open-source mediante gobernanza comunitaria.`
        }
      ]
    },

    {
      id: "tokenizacion",
      heading: "Tokenización de Activos Reales e Industriales",
      text: `En 2025, la tokenización de activos físicos mediante ERC-20 se ha expandido más allá de las finanzas, 
alcanzando sectores como energía, salud, educación y logística.`,
      list: [
        {
          title: "Casos industriales",
          text: `- **Siemens Energy** y **Shell** tokenizan créditos de carbono en Ethereum.  
- **BMW Group** usa tokens ERC-20 para rastrear la procedencia de componentes y materiales sostenibles.  
- **Maersk Logistics** integra contratos ERC-20 para digitalizar certificados de embarque y trazabilidad marítima.`
        },
        {
          title: "Aplicaciones educativas y sociales",
          text: `- **UVEG** y **CoachBlockchain** implementan tokens ERC-20 como microcredenciales verificables en la cadena.  
- Instituciones de América Latina exploran **“tokenes de reputación”** para certificar logros académicos y profesionales.`
        },
        {
          title: "Mercados emergentes",
          text: `Los gobiernos de **Emiratos Árabes, Singapur y México** impulsan programas piloto para emitir bonos verdes tokenizados 
y subsidios digitales a productores agrícolas, garantizando trazabilidad y reducción de corrupción.`
        }
      ]
    },

    {
      id: "conclusion",
      heading: "Conclusión: Una Infraestructura para el Futuro Digital",
      text: `Los tokens ERC-20 no solo son herramientas financieras, sino los **bloques constructivos** de una nueva economía descentralizada.  
Permiten representar cualquier tipo de valor —monetario, social, educativo o ambiental— de forma programable y verificable.  
En 2025, su uso abarca desde **billeteras institucionales y stablecoins reguladas**, hasta **credenciales académicas tokenizadas y mercados verdes globales**.  
El estándar ERC-20 se mantiene como la base más sólida de la tokenización moderna y seguirá siendo esencial para conectar 
el mundo físico y el digital bajo un mismo lenguaje: el de la confianza criptográfica.`
    }
  ]
};

export default CAP22;
